import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { messages, pricing } from 'app/constants';
import { DataService, FormatService, HelperService, HTTPService } from 'app/services';
import { debounce } from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-tire-storage-details',
  templateUrl: './tire-storage-details.component.html',
  styleUrls: ['./tire-storage-details.component.scss'],
})
export class TireStorageDetailsComponent implements OnInit, OnDestroy {
  isLoadingTireStorageModal = false;
  selectedTab: number = 0;
  selectedSidebarTab = 'basic';
  activityLogs = [];
  focus: any = {};
  isProcessing: boolean = false;
  isLoading: boolean = false;
  isNewCategoriesEnabled: boolean = false;
  isSalesCodeEnabled: boolean = false;
  canEditClassifications: boolean = false;
  pricingOptions: any[] = [
    {
      label: 'Per Tire',
      name: 'Per Tire',
      value: 'perTire',
      placeholder: 'Per Tire',
    },
    {
      label: 'Flat Fee',
      name: 'Flat Fee',
      value: 'flatFee',
      placeholder: 'Flat Fee',
    },
  ];
  tireStorage: any = {
    _id: '',
    title: '',
    titleFR: '',
    count: '',
    pricingType: this.pricingOptions[1],
    cost: '',
    price: '',
    internalNotes: '',
    isTaxable: true,
    displayHoursOnOrder: true,
    displayDescOnOrder: true,
    commissionable: true,
    status: true,
  };
  errors: any = {
    title: '',
    titleFR: '',
    cost: 0,
    price: 0,
  };
  checkUniqueTitle = debounce(this._checkUniqueTitle, 600);
  currentLang: string = 'en';
  classificationPrivilegeMsg: string = messages.classificationPrivilegeMsg;
  localizationSubscription: Subscription;
  companyLocalization: boolean = false;
  isTitleUnique: boolean = true;
  isHide: boolean = false;
  cannedInspections: any = []
  isOpenFromRO: boolean = false;
  isFinalizedRo: boolean = false;
  invoicedRO: boolean = false;

  constructor(
    private dialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dataService: DataService,
    private formatService: FormatService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isNewCategoriesEnabled = this.dataService.isNewItemCategoriesEnable;
    this.isSalesCodeEnabled = this.dataService.isSalesCodeEnabled;
    this.canEditClassifications = this.dataService.canEditClassifications;

    if (this.dialogConfig.data) {
      this.isOpenFromRO = this.dialogConfig.data?.isOpenFromRO;
      this.isFinalizedRo = this.dialogConfig.data?.isFinalizedRo;
      this.invoicedRO = this.dialogConfig.data?.invoicedRO;
      if (this.dialogConfig.data?.tireStorage) { 
        this.setTireStorage(this.dialogConfig.data.tireStorage) 
      } else if(!this.isOpenFromRO) {
        this.getAllCategories();
      };
    }

    if(!this.isOpenFromRO) this.getTireStorageInspections();
    this.getLocalizationFlags();
    
  }

  getAllTemplatesCount(){
    const conf = {
      offset: 1,
      limit: 15,
      activeStatus: 'all',
    };
    this.httpService
      .getItems({ ...conf, type: 'tireStorageTemplate' })
      .then(
        (data: any) => {
          if (data.records) {
            this.dialogConfig.data.totalCount = data.totalCount;
            this.setDefaultTitle();
          }
        },
        (error) => {
          this.helperService.showToaster('error', error);
        },
      )
  }

  getAllCategories() {
    const query: any = { itemType: 'tireStorage' };
    this.isLoadingTireStorageModal = true;
    this.httpService
      .getPricingCategories(query)
      .then((res: any) => {
        if (res?.categories && res?.categories?.length) {
          const category = res.categories[0];
          this.tireStorage.category = {
            categoryId: category?.categoryId,
            categoryName: category?.categoryName,
            name: category?.categoryName,
            _id: category?._id
          };
          if (this.isNewCategoriesEnabled && this.isSalesCodeEnabled) {
            if (category?.salesCode && category.salesCode?.salesCodeId) this.tireStorage.salesCode = category.salesCode;
            else this.tireStorage.salesCode = {};
          }
        }
      })
      .finally(() => {
        this.isLoadingTireStorageModal = false;
      });
  }

  setTireStorage(tireStorage) {
    this.tireStorage = {
      _id: tireStorage._id,
      title: tireStorage.title,
      titleFR: tireStorage.titleFR,
      count: tireStorage.count || '',
      pricingType: tireStorage.pricingType === 'perTire' ? this.pricingOptions[0] : this.pricingOptions[1],
      cost: tireStorage.cost,
      price: tireStorage.price,
      associatedInspection: tireStorage.associatedInspection,
      internalNotes: tireStorage.internalNotes,
      isTaxable: tireStorage.isTaxable,
      displayHoursOnOrder: tireStorage.displayHoursOnOrder,
      displayDescOnOrder: tireStorage.displayDescOnOrder,
      commissionable: tireStorage.commissionable,
      status: tireStorage?.status || true,
    };

    if (this.isNewCategoriesEnabled && this.isSalesCodeEnabled) {
      if (tireStorage.salesCode && tireStorage.salesCode?.salesCodeId) this.tireStorage.salesCode = tireStorage.salesCode;
      else this.tireStorage.salesCode = {};
    }

    if (this.isNewCategoriesEnabled && tireStorage.category && tireStorage.category.categoryId) {
      this.tireStorage.category = {
        ...tireStorage.category,
        categoryId: tireStorage.category.categoryId
      };
    }
  }

  closeDialog(close?) {
    this.dynamicDialogRef.close({ isSuccess: false });
  }

  getLocalizationFlags() {
    const { isMultiLocationEnabled } = this.helperService.getCompany();
    this.localizationSubscription = this.dataService.getCompanyLocalization().subscribe((value) => {
      if (value) {
        this.companyLocalization = value;
        if (!isMultiLocationEnabled) {
          this.httpService.getCompanyPreferredLanguage().then((res: any) => {
            const lang = res.preferredLanguage;
            if (lang) {
              this.currentLang = lang.toLowerCase();
            }
          });
        } else {
          this.httpService.getCompanyDetails(this.helperService.getCompany().companyId).then((company: any) => {
            const lang = company.shop.preferredLanguage;
            if (lang) this.currentLang = lang.toLowerCase();
          });
        }

      }
      if(!this.dialogConfig.data?.tireStorage) this.getAllTemplatesCount();
    });
  }

  getTireStorageInspections(){
    this.httpService.getCannedInspection({}).then(
      (cannedInspections: any) => {
        if (cannedInspections.totalCount >= 0) {
          const inspections = cannedInspections.records.filter(element => element.isTireStorageInspection);
          if(inspections?.length) this.cannedInspections = inspections.map(({_id, title}) => ({inspectionId: _id, name: title}))
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
        // this.loading = false;
      }
    );
  }

  setDefaultTitle() {
    if ((this.dialogConfig.data?.totalCount || this.dialogConfig.data?.totalCount === 0) && !this.dialogConfig.data?.tireStorage) {
      this.tireStorage.title = `Package# ${this.dialogConfig.data?.totalCount + 1}`;
      if (this.companyLocalization) this.tireStorage.titleFR = `Offre# ${this.dialogConfig.data?.totalCount + 1}`;
    }
  }

  onTabSelect(event) {
    this.selectedTab = event;
    this.selectedSidebarTab = '';
    if (this.selectedTab === 1) {
      this.getLogs();
    }
  }

  getLogs() {
    // /appLogs/getByQuery
    const query = <any>{};
    // if (this.filterOption?.length) {
    //   query.entityType = this.filterOption;
    // }
    query.entityId = this.tireStorage._id;
    query.isItemLogs = true;
    this.httpService.getActivityLog(query).then(
      (data: any) => {
        console.log(data);
        if (data) {
          this.activityLogs = this.formatService.formateCatlogActivityLogs(data);
        }
        // this.isFilterLoading = false;
      },
      (err) => {
        this.helperService.showToaster('error', err);
        // this.isFilterLoading = false;
      },
    );
  }

  scrollToSection(type): void {
    this.selectedTab = 0;
    this.selectedSidebarTab = '';
    const element = document.getElementById(type);
    if (element) {
      this.selectedSidebarTab = type;
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  focusInput(type: string) {
    this.focus[type] = true;
  }

  blurInput(type?: string, value?) {
    this.errors[type] = '';
    // if (['title', 'titleFR'].includes(type) && this.tireStorage.title) {
    //   this.checkUniqueTitle();
    // }
    if(type === 'count'){
      if(this.tireStorage.count > 6){
        this.helperService.showToaster('error', messages.tireStorageCountMessage);
        setTimeout(() => {
          this.tireStorage.count = '6';
          this.cdr.detectChanges();
        }, 500);
      }
    }
    this.focus[type] = false;
  }

  isNumber(str) {
    var pattern = /^\d+\.?\d*$/;
    return pattern.test(str); // returns a boolean
  }

  handleChange(name: string, value: any) {
    this.tireStorage[name] = value;

    if(name === 'associatedInspection') this.errors[name] = '';

    if (name === 'title' || name === 'titleFR') {
      this.checkUniqueTitle();
    }
  }

  _checkUniqueTitle() {
    if (!this.tireStorage.title.trim()) return;

    const query = { title: this.tireStorage.title.trim(), type: 'tireStorageTemplate', code: '' };

    this.httpService.isItemTitleUnique(query, this.tireStorage._id).then(
      (res) => {
        if (!res) {
          this.errors.title = 'A tire storage item with the same description already exists. Please provide a unique description.';
          this.isTitleUnique = false;
          this.helperService.showToaster('error', this.errors.title);
        } else {
          this.errors.title = '';
          this.isTitleUnique = true;
        }
      },
      (err) => {
        console.error(err);
      },
    );
  }

  onAutoCompleteSelect(event, name) {}

  searchSuggestion(event, name) {}

  onClearSuggestion(type) {}

  showSuggestions(type) {}

  openAssignSalesCodeDialog() {
    if (!this.canEditClassifications) {
      return;
    }
    const data = {
      item: this.tireStorage,
      openFrom: 'tireStorage-modal',
    };
    data.item.title = data.item?.title || '';
    this.dataService.sendData(this.dataService.typeDialog('open-sales-code-assign-modal', 'tireStorage-modal', null, null, data));
  }

  validateTireStorage() {
    let isTireStorageDataValid = true;
    if(this.tireStorage.count > 6){
      this.helperService.showToaster('error', messages.tireStorageCountMessage);
      isTireStorageDataValid = false;
      return;
    }
    if (this.companyLocalization && this.currentLang === 'fr') {
      if (!this.tireStorage.titleFR) {
        this.errors.titleFR = 'A line item description must exist';
        isTireStorageDataValid = false;
        //this.helperService.showToaster('error', 'Please add basic information.');
        //this.scrollToSection('basic');
        //return; // added to avoid multiple scrolling;
      }
    } else {
      if (!this.tireStorage.title) {
        this.errors.title = 'A line item description must exist';
        isTireStorageDataValid = false;
        //this.helperService.showToaster('error', 'Please add basic information.');
        //this.scrollToSection('basic');
        //return; // added to avoid multiple scrolling;
      }
    }

    if (!this.isTitleUnique) {
      this.errors.title = 'A tire storage item with the same description already exists. Please provide a unique description.';
      isTireStorageDataValid = false;
      //this.helperService.showToaster('error', this.errors.title);
      //this.scrollToSection('basic');
      //return; // added to avoid multiple scrolling;
    }

    if (!this.tireStorage.associatedInspection?.inspectionId) {
      this.errors.associatedInspection = 'Inspection is required';
      isTireStorageDataValid = false;
      //this.helperService.showToaster('error', 'Please add basic information.');
      //this.scrollToSection('basic');
      //return; // added to avoid multiple scrolling;
    }

    // changed validation condition
    if (this.tireStorage.cost === '') {
      this.errors.cost = 'Cost is required';
      isTireStorageDataValid = false;
      //this.helperService.showToaster('error', 'Please add pricing information.');
      //this.scrollToSection('pricing');
      //return; // added to avoid multiple scrolling;
    }
    if (this.tireStorage.price === '') {
      this.errors.price = 'Price is required';
      isTireStorageDataValid = false;
      //this.helperService.showToaster('error', 'Please add pricing information.');
      //this.scrollToSection('pricing');
      //return; // added to avoid multiple scrolling;
    }

    if(!isTireStorageDataValid) {
      this.helperService.showToaster('error', 'Please add required information.')
    }

    return isTireStorageDataValid;
  }

  onSubmit() {
    this.errors = {};

    if (this.validateTireStorage()) {
      if (this.tireStorage._id) this.updateTireStorage(this.makeTireStoragePayload());
      else this.createTireStorage(this.makeTireStoragePayload());
    }
  }

  makeTireStoragePayload(isOpenCreate = false) {
    const tireStorage: any = {
      _id: this.tireStorage._id || '',
      title: this.tireStorage.title,
      titleFR: this.tireStorage.titleFR,
      count: Number(this.tireStorage.count),
      pricingType: this.tireStorage.pricingType?.value,
      type: 'tireStorageTemplate',
      associatedInspection: this.tireStorage.associatedInspection,
      isTaxable: this.tireStorage.isTaxable,
      //we only update cost when the cost type is cost - ALP-15794
      cost: Number(this.tireStorage.cost),
      price: Number(this.tireStorage.price),
      internalNotes: this.tireStorage.internalNotes || '',
      locationQboItemRefId: this.tireStorage.locationQboItemRefId || {},
      displayHoursOnOrder: this.tireStorage.displayHoursOnOrder,
      displayDescOnOrder: this.tireStorage.displayDescOnOrder,
      commissionable: this.tireStorage.commissionable,
      status: this.tireStorage.status,
    };

    if (this.isNewCategoriesEnabled && this.isSalesCodeEnabled) {
      if (this.tireStorage.salesCode && this.tireStorage.salesCode?.salesCodeId) tireStorage.salesCode = this.tireStorage.salesCode;
      else tireStorage.salesCode = {};
    }

    if (this.tireStorage.category && this.isNewCategoriesEnabled) {
      tireStorage.category = {
        ...this.tireStorage.category,
        name: this.tireStorage.category.name,
        categoryId: this.tireStorage.category.categoryId || '',
      };
    }

    return tireStorage;
  }

  createTireStorage(tireStorage: any) {
    delete tireStorage._id;
    if (this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    this.httpService
      .createItem({
        ...tireStorage,
      })
      .then(
        (res: any) => {
          this.isProcessing = false;
          this.helperService.showToaster('success', messages.tireStorageSaved);
          this.dynamicDialogRef.close({ isSuccess: true });
        },
        (err) => {
          this.isProcessing = false;
          this.helperService.showToaster('error', err);
        },
      );
  }

  updateTireStorage(tireStorage: any) {
    if (this.isProcessing) {
      return;
    }
    if(this.isOpenFromRO){
      const data: any = { isSuccess: true }
      data.tireStorageTemplate = tireStorage;
      this.dynamicDialogRef.close(data);
      return;
    } 
    this.isProcessing = true;

    this.httpService
      .updateItem({
        ...tireStorage,
      })
      .then(
        (res: any) => {
          this.isProcessing = false;
          this.helperService.showToaster('success', messages.tireStorageSaved);
          const data: any = { isSuccess: true }
          // if(this.isOpenFromRO) data.tireStorageTemplate = tireStorage;
          this.dynamicDialogRef.close(data);
        },
        (err) => {
          this.isProcessing = false;
          this.helperService.showToaster('error', err);
        },
      );
  }

  ngOnDestroy() {
    if (this.localizationSubscription) this.localizationSubscription.unsubscribe();
  }
}
